/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {currencyNumberFormatNoDigit, translate} from "utils/utility";
import {Article} from "types/entities/Article";
import clsx from "clsx";
import {ArticleType} from "types/enums/ArticleType";
import Yearly12MonthsDescription
    from "pages/Front/Booking/components/YearlyPackage/components/Yearly12MonthsDescription";
import Yearly6MonthsDescription from "pages/Front/Booking/components/YearlyPackage/components/Yearly6MonthsDescription";

interface GiftCardArticleCardProps {
    articles: Article[];
    onClick: (article: Article) => void;
    showDescription?: boolean;
    grid?: boolean;
}

const GiftCardArticleCard = (props: GiftCardArticleCardProps) => {
    const {t} = useTranslation();

    return (
        <div className="GiftCardArticleCard w-full flex justify-center flex-col items-center">
            <div className={clsx(props.grid ? "grid sm:grid-cols-3 gap-6" : "flex flex-col sm:flex-row gap-6 w-full")}>
                {props.articles.map((article, index) => {
                    const isYearlyVoucher: boolean = article.type === ArticleType.Yearly;
                    return (
                        <div key={index} className={"ArticleCard flex flex-col justify-start items-center sm:basis-1/2"}>
                            <div className={"w-full"}>
                                <div className={"select-none rounded-2xl gap-y-2 flex flex-col leading-[1] text-center mx-auto"}>
                                    <div
                                        className={
                                            "text-black cursor-pointer rounded-2xl text-sm bg-gradient-to-b from-[#f7f7f7] to-white pt-4 pb-3 sm:pt-3 sm:pb-2 lg:pt-5 lg:pb-4 capitalize font-semibold px-2 lg:px-4 h-full flex flex-col space-y-3 justify-center items-center"
                                        }
                                        onClick={() => props.onClick(article)}
                                    >
                                        <div
                                            className={"flex font-zuume text-4xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-[40pt] font-bold italic space-x-1"}
                                        >
                                            <span>{currencyNumberFormatNoDigit(article.article_value)}</span>
                                            <span>.-</span>
                                        </div>
                                        {article.active_minutes_after_purchase > 1440 && (
                                            <div className={"text-xl font-black font-poppins uppercase"}>{translate(article.article_name)}</div>
                                        )}
                                    </div>
                                    <button
                                        className={clsx(
                                            "ButtonCTA duration-150 transform-gpu",
                                            "rounded-2xl hover:drop-shadow-none drop-shadow-[0px_5px_5px_rgba(0,0,0,0.20)] mix-blend-multiply bg-gradient-to-l from-[#F7C12A] to-[#ED3D34] hover:text-white text-black text-base  lg:text-lg xl:text-xl py-3 px-6 font-bold"
                                        )}
                                        onClick={() => props.onClick(article)}
                                    >
                                        <span>{t("Add to cart")}</span>
                                    </button>
                                </div>
                            </div>
                            {props.showDescription && isYearlyVoucher && (
                                <>
                                    {article.active_minutes_after_purchase == 525960 && (<Yearly12MonthsDescription />)}
                                    {article.active_minutes_after_purchase == 262980 && (<Yearly6MonthsDescription />)}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default GiftCardArticleCard;
